import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, X, Loader2, Truck, PackageCheck, Ban, Camera } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { apiGet, apiPost } from '../../lib/api';
import MaterialItemSelector from '../MaterialItemSelector';

const PILL = {
  draft: 'bg-slate-100 text-slate-600', dispatched: 'bg-amber-50 text-amber-700', received: 'bg-emerald-50 text-emerald-700',
  short_received: 'bg-rose-50 text-rose-700', cancelled: 'bg-slate-100 text-slate-400 line-through',
};
const field = 'border border-slate-300 rounded-sm px-3 py-2 text-sm w-full bg-white focus:outline-none focus:border-[#3b4872]';

function readPhoto(file) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
}

/** Camera-only capture (mobile opens the camera; desktop falls back to file picker). */
function PhotoCapture({ value, onChange, label, testid }) {
  return (
    <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer border border-dashed border-slate-300 rounded-sm px-3 py-2">
      <Camera size={14} /> {value ? 'Photo captured ✓' : label}
      <input type="file" accept="image/*" capture="environment" className="hidden" data-testid={testid}
        onChange={async e => { const f = e.target.files?.[0]; if (f) onChange(await readPhoto(f)); }} />
    </label>
  );
}

export function TransfersTab({ projectId = '', compact = false, inboundOnly = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [direction, setDirection] = useState(inboundOnly ? 'inbound' : '');
  const [status, setStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [receiving, setReceiving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet('/stores/transfers', { project_id: projectId || undefined, direction: direction || undefined, status: status || undefined, page: 1, limit: 200 });
      setRows(d.items || []);
    } catch { setRows([]); } finally { setLoading(false); }
  }, [projectId, direction, status]);
  useEffect(() => { load(); }, [load]);

  const cancel = async (t) => {
    const reason = window.prompt('Reason for cancelling this transfer?');
    if (reason === null) return;
    try { await apiPost(`/stores/transfers/${t.id}/cancel`, { reason }); toast.success(`${t.transfer_no} cancelled — source stock restored`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Cancel failed'); }
  };

  return (
    <div className="space-y-3" data-testid="transfers-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><Truck size={18} /> Material Transfers</h2>
          <p className="text-xs text-slate-500">Godown ↔ site and site ↔ site. Stock is in transit until the destination confirms receipt.</p>
        </div>
        <div className="flex items-center gap-2">
          {!inboundOnly && (
            <select value={direction} onChange={e => setDirection(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded text-sm" data-testid="transfer-direction-filter">
              <option value="">All</option><option value="inbound">Inbound</option><option value="outbound">Outbound</option>
            </select>
          )}
          <select value={status} onChange={e => setStatus(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded text-sm" data-testid="transfer-status-filter">
            <option value="">All statuses</option>
            {Object.keys(PILL).map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
          {!inboundOnly && (
            <Button onClick={() => setShowCreate(v => !v)} className="bg-[#3b4872] hover:bg-[#2d3856]" data-testid="transfer-new-btn"><Plus size={14} className="mr-1" /> New transfer</Button>
          )}
        </div>
      </div>
      {showCreate && <CreateTransferForm projectId={projectId} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {receiving && <ReceiveTransferForm transfer={receiving} onClose={() => setReceiving(null)} onDone={() => { setReceiving(null); load(); }} />}
      {compact ? (
        <div className="space-y-2" data-testid="transfers-card-list">
          {loading ? <div className="text-center py-6 text-slate-400 text-sm">Loading…</div>
            : rows.length === 0 ? <div className="text-center py-6 text-slate-400 text-sm">No transfers</div>
            : rows.map(t => (
              <div key={t.id} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`transfer-card-${t.transfer_no}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs font-semibold">{t.transfer_no}</span>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${PILL[t.status] || ''}`} data-testid={`transfer-status-${t.transfer_no}`}>{t.status === 'dispatched' ? 'in transit' : t.status.replace('_', ' ')}</span>
                </div>
                <div className="text-xs text-slate-600 mt-1.5">{t.from?.name} <span className="text-slate-400">→</span> <b>{t.to?.name}</b></div>
                <div className="text-[11px] text-slate-500 mt-1.5 space-y-0.5">
                  {(t.items || []).map((l, i) => <div key={i}>{l.description} — {l.qty_sent} {l.uom}{l.qty_received != null && l.qty_received !== l.qty_sent ? <span className="text-rose-600"> (recv {l.qty_received})</span> : null}</div>)}
                </div>
                <div className="flex items-center justify-between mt-2 text-[11px] text-slate-400">
                  <span>{(t.dispatched_at || t.created_at || '').slice(0, 10)} · {t.vehicle_no || '—'}</span>
                  {t.total_value != null && <span className="font-semibold text-slate-600">₹{Number(t.total_value).toLocaleString('en-IN')}</span>}
                </div>
                {t.status === 'dispatched' && (
                  <Button className="w-full mt-3 bg-emerald-700 hover:bg-emerald-800" onClick={() => setReceiving(t)} data-testid={`transfer-receive-${t.transfer_no}`}>
                    <PackageCheck size={14} className="mr-1.5" /> Receive
                  </Button>
                )}
              </div>
            ))}
        </div>
      ) : (
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-xs text-slate-600"><tr>
            <th className="text-left px-3 py-2">Transfer</th><th className="text-left px-3 py-2">From → To</th>
            <th className="text-left px-3 py-2">Items</th><th className="text-right px-3 py-2">Value</th>
            <th className="text-center px-3 py-2">Status</th><th className="text-center px-3 py-2">Action</th>
          </tr></thead>
          <tbody className="divide-y">
            {loading ? <tr><td colSpan={6} className="text-center py-6 text-slate-400">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={6} className="text-center py-6 text-slate-400">No transfers</td></tr>
              : rows.map(t => (
                <tr key={t.id} className="align-top hover:bg-slate-50" data-testid={`transfer-row-${t.transfer_no}`}>
                  <td className="px-3 py-2 font-mono text-xs">{t.transfer_no}<div className="text-[10px] text-slate-400 font-sans">{(t.dispatched_at || t.created_at || '').slice(0, 10)} · {t.vehicle_no || '—'}</div></td>
                  <td className="px-3 py-2 text-xs">{t.from?.name} <span className="text-slate-400">→</span> <b>{t.to?.name}</b></td>
                  <td className="px-3 py-2 text-xs">{(t.items || []).map((l, i) => <div key={i}>{l.description} — {l.qty_sent} {l.uom}{l.qty_received != null && l.qty_received !== l.qty_sent ? <span className="text-rose-600"> (recv {l.qty_received})</span> : null}</div>)}</td>
                  <td className="px-3 py-2 text-right text-xs">{t.total_value != null ? `₹${Number(t.total_value).toLocaleString('en-IN')}` : '—'}</td>
                  <td className="px-3 py-2 text-center"><span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full ${PILL[t.status] || ''}`} data-testid={`transfer-status-${t.transfer_no}`}>{t.status === 'dispatched' ? 'in transit' : t.status.replace('_', ' ')}</span></td>
                  <td className="px-3 py-2 text-center whitespace-nowrap">
                    {t.status === 'dispatched' && <Button size="sm" variant="outline" onClick={() => setReceiving(t)} data-testid={`transfer-receive-${t.transfer_no}`}><PackageCheck size={14} className="mr-1" /> Receive</Button>}
                    {['draft', 'dispatched'].includes(t.status) && !inboundOnly && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => cancel(t)} data-testid={`transfer-cancel-${t.transfer_no}`}><Ban size={14} /></Button>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

function CreateTransferForm({ projectId, onClose, onCreated }) {
  const [projects, setProjects] = useState([]);
  const [godown, setGodown] = useState([]);
  const [fromType, setFromType] = useState('project');
  const [fromId, setFromId] = useState(projectId || '');
  const [toType, setToType] = useState('project');
  const [toId, setToId] = useState('');
  const [items, setItems] = useState([]);          // project source (MaterialItemSelector)
  const [gLines, setGLines] = useState([]);        // godown source [{id, qty}]
  const [vehicle, setVehicle] = useState('');
  const [challan, setChallan] = useState('');
  const [remarks, setRemarks] = useState('');
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet('/projects').then(r => setProjects(Array.isArray(r) ? r : (r?.items || []))).catch(() => {});
    apiGet('/stores/opening-stock', { limit: 500 }).then(r => setGodown(r?.items || [])).catch(() => {});
  }, []);

  const submit = async () => {
    const lines = fromType === 'godown'
      ? gLines.filter(l => Number(l.qty) > 0).map(l => ({ opening_stock_id: l.id, qty: Number(l.qty) }))
      : items.map(i => ({ line_key: i.boq_id, qty: i.quantity }));
    if (fromType === 'project' && !fromId) return toast.error('Select the source project');
    if (toType === 'project' && !toId) return toast.error('Select the destination project');
    if (!lines.length) return toast.error('Add at least one line');
    setSaving(true);
    try {
      // Item i (Emergent_Procurement doc): a godown transfer previously
      // always hard-coded "Central Godown" as the source location, never the
      // actual location the stock was filed under.
      const fromLocationName = fromType === 'godown'
        ? (godown.find(g => g.id === lines[0]?.opening_stock_id)?.location || undefined)
        : undefined;
      const t = await apiPost('/stores/transfers', {
        from: fromType === 'godown' ? { type: 'godown', name: fromLocationName } : { type: 'project', id: fromId },
        to: toType === 'godown' ? { type: 'godown' } : { type: 'project', id: toId },
        items: lines, vehicle_no: vehicle || undefined, challan_no: challan || undefined, remarks: remarks || undefined,
        dispatch_now: true, evidence_photo: photo || undefined,
      });
      toast.success(`${t.transfer_no} dispatched — in transit to ${t.to?.name}`);
      onCreated?.(t);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Transfer failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="transfer-create-form">
      <div className="flex items-center justify-between"><h3 className="font-display font-bold text-slate-800">New material transfer</h3>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button></div>
      <div className="grid gap-3 md:grid-cols-4">
        <label className="text-xs text-slate-600">From
          <select value={fromType} onChange={e => { setFromType(e.target.value); setItems([]); setGLines([]); }} className={field} data-testid="transfer-from-type"><option value="project">Site (project)</option><option value="godown">Central Godown</option></select></label>
        {fromType === 'project' && <label className="text-xs text-slate-600">Source project
          <select value={fromId} onChange={e => { setFromId(e.target.value); setItems([]); }} className={field} data-testid="transfer-from-project"><option value="">Select…</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>}
        <label className="text-xs text-slate-600">To
          <select value={toType} onChange={e => setToType(e.target.value)} className={field} data-testid="transfer-to-type"><option value="project">Site (project)</option><option value="godown">Central Godown</option></select></label>
        {toType === 'project' && <label className="text-xs text-slate-600">Destination project
          <select value={toId} onChange={e => setToId(e.target.value)} className={field} data-testid="transfer-to-project"><option value="">Select…</option>{projects.filter(p => p.id !== fromId).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>}
      </div>
      {fromType === 'project'
        ? <MaterialItemSelector projectId={fromId} mode="transfer" selectedItems={items} onItemsChange={setItems} />
        : (
          <div className="border rounded-md max-h-64 overflow-y-auto divide-y" data-testid="transfer-godown-picker">
            {godown.map(g => {
              const sel = gLines.find(l => l.id === g.id);
              return (
                <div key={g.id} className={`flex items-center gap-3 px-3 py-2 text-sm ${sel ? 'bg-indigo-50/60' : ''}`}>
                  <input type="checkbox" checked={!!sel} onChange={() => setGLines(cur => sel ? cur.filter(l => l.id !== g.id) : [...cur, { id: g.id, qty: '' }])} data-testid={`transfer-godown-${g.item_code}`} />
                  <div className="flex-1 min-w-0 truncate">{g.material_name} <span className="text-slate-400 text-xs">· {g.item_code} · {g.location || 'Central Godown'} · available {g.qty} {g.uom}</span></div>
                  {sel && <Input type="number" min="0" max={g.qty} step="any" value={sel.qty} placeholder="Qty" className="h-7 w-24"
                    onChange={e => setGLines(cur => cur.map(l => l.id === g.id ? { ...l, qty: Math.min(Number(e.target.value), g.qty) } : l))} data-testid={`transfer-godown-qty-${g.item_code}`} />}
                </div>
              );
            })}
          </div>
        )}
      <div className="grid gap-3 md:grid-cols-3">
        <Input value={vehicle} onChange={e => setVehicle(e.target.value)} placeholder="Vehicle no." data-testid="transfer-vehicle" />
        <Input value={challan} onChange={e => setChallan(e.target.value)} placeholder="Challan no." data-testid="transfer-challan" />
        <Input value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Remarks" data-testid="transfer-remarks" />
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <PhotoCapture value={photo} onChange={setPhoto} label="Capture loading photo" testid="transfer-dispatch-photo" />
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="button" disabled={saving} onClick={submit} className="bg-[#3b4872] hover:bg-[#2d3856]" data-testid="transfer-submit">{saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Truck size={14} className="mr-1" />} Dispatch</Button>
        </div>
      </div>
    </div>
  );
}

function ReceiveTransferForm({ transfer, onClose, onDone }) {
  const [lines, setLines] = useState((transfer.items || []).map(l => ({ qty: l.qty_sent, reason: '' })));
  const [photo, setPhoto] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const r = await apiPost(`/stores/transfers/${transfer.id}/receive`, {
        lines: lines.map((l, i) => ({ line_index: i, qty_received: Number(l.qty), shortage_reason: l.reason || undefined })),
        remarks: remarks || undefined, evidence_photo: photo || undefined,
      });
      toast.success(`${transfer.transfer_no} ${r.status === 'short_received' ? 'received short — discrepancy recorded' : 'received — stock is now available here'}`);
      onDone?.(r);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Receipt failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-emerald-200 rounded-md p-4 space-y-3" data-testid="transfer-receive-form">
      <div className="flex items-center justify-between"><h3 className="font-display font-bold text-slate-800">Receive {transfer.transfer_no} at {transfer.to?.name}</h3>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button></div>
      <div className="border rounded-md divide-y">
        {(transfer.items || []).map((l, i) => {
          const short = Number(lines[i].qty) < l.qty_sent;
          return (
            <div key={i} className="px-3 py-2 text-sm flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[180px]">{l.description} <span className="text-slate-400 text-xs">sent {l.qty_sent} {l.uom}</span></div>
              <Input type="number" min="0" max={l.qty_sent} step="any" value={lines[i].qty} className="h-8 w-28"
                onChange={e => setLines(cur => cur.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} data-testid={`transfer-recv-qty-${i}`} />
              {short && <Input value={lines[i].reason} placeholder="Shortage reason (required)" className="h-8 w-56"
                onChange={e => setLines(cur => cur.map((x, j) => j === i ? { ...x, reason: e.target.value } : x))} data-testid={`transfer-recv-reason-${i}`} />}
            </div>
          );
        })}
      </div>
      <Input value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Receipt remarks" data-testid="transfer-recv-remarks" />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <PhotoCapture value={photo} onChange={setPhoto} label="Capture unloading photo" testid="transfer-receipt-photo" />
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="button" disabled={saving} onClick={submit} className="bg-emerald-700 hover:bg-emerald-800" data-testid="transfer-recv-submit">{saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <PackageCheck size={14} className="mr-1" />} Confirm receipt</Button>
        </div>
      </div>
    </div>
  );
}

export default TransfersTab;
